import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL, PRIORITY_LABEL, groupStatusCounts } from "@/lib/constants";
import StatusBadge from "@/components/common/StatusBadge";
import PriorityBadge from "@/components/common/PriorityBadge";
import SLATimer from "@/components/common/SLATimer";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import { AlertCircle, CheckCircle2, FileEdit, Flame, Layers, Users } from "lucide-react";

const GOLD = "#d4af37";

const STATUS_CHART_COLORS = {
  draft: "#64748b",
  submitted: "#d4af37",
  assigned: "#3b82f6",
  accepted: "#06b6d4",
  in_progress: "#8b5cf6",
  under_investigation: "#6366f1",
  waiting_for_client: "#f59e0b",
  waiting_for_access: "#f97316",
  waiting_for_vendor: "#eab308",
  development_required: "#a855f7",
  testing_in_progress: "#14b8a6",
  scheduled_for_release: "#0ea5e9",
  on_hold: "#94a3b8",
  escalated: "#ef4444",
  resolution_submitted: "#10b981",
  returned_to_engineer: "#fb7185",
  returned_for_information: "#f97316",
  reopened: "#ec4899",
  rejected: "#e11d48",
  duplicate: "#64748b",
  cancelled: "#78716c",
  closed: "#16a34a",
};

function statusChartColor(status, index = 0) {
  const fallback = [
    "#d4af37",
    "#3b82f6",
    "#8b5cf6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#06b6d4",
    "#ec4899",
  ];

  return STATUS_CHART_COLORS[status] || fallback[index % fallback.length];
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data));
  }, []);
  if (!data) return <DashboardSkeleton />;

  const statusData = groupStatusCounts(data.by_status).map((bar, index) => ({
    ...bar,
    color: statusChartColor(bar.statuses[0], index),
  }));
  const priorityData = (data.by_priority || []).map((s) => ({
    name: s._id || "P?",
    value: s.count,
  }));
  const appData = (data.by_application || []).map((s) => ({
    name: s._id || "—",
    value: s.count,
  }));

  const defaultKpis = [
    { label: "Total tickets", value: data.total, icon: Layers, tone: "text-foreground", testid: "kpi-total", href: "/tickets" },
    { label: "Open", value: data.open, icon: FileEdit, tone: "text-sky-300", testid: "kpi-open", href: "/tickets?open=true" },
    { label: "Critical (P1)", value: data.critical, icon: Flame, tone: "text-rose-300", testid: "kpi-critical", href: "/tickets?priority=P1&open=true" },
    { label: "SLA at risk", value: data.sla_at_risk, icon: AlertCircle, tone: "text-amber-300", testid: "kpi-sla", href: "/tickets?sla=at_risk" },
    { label: "Closed", value: data.closed, icon: CheckCircle2, tone: "text-emerald-300", testid: "kpi-closed", href: "/tickets?status=closed" },
  ];

  const organizationAdminKpis = [
    { label: "Client users", value: data.organization_users ?? 0, icon: Users, tone: "text-violet-300", testid: "kpi-org-users", href: "/admin/users" },
    { label: "Assigned applications", value: data.assigned_applications ?? 0, icon: Layers, tone: "text-[#e5c158]", testid: "kpi-org-applications", href: "/admin/organizations" },
    { label: "Open tickets", value: data.open, icon: FileEdit, tone: "text-sky-300", testid: "kpi-open", href: "/tickets?open=true" },
    {
      label: "Client confirmation pending",
      value:
        (data.by_status || []).find(
          (item) => item._id === "waiting_for_client"
        )?.count ?? 0,
      icon: AlertCircle,
      tone: "text-orange-300",
      testid: "kpi-waiting-client",
      href: "/tickets?status=waiting_for_client",
    },
    { label: "Closed tickets", value: data.closed, icon: CheckCircle2, tone: "text-emerald-300", testid: "kpi-closed", href: "/tickets?status=closed" },
    { label: "SLA at risk", value: data.sla_at_risk, icon: Flame, tone: "text-rose-300", testid: "kpi-sla", href: "/tickets?sla=at_risk" },
  ];

  const kpis = data.role === "client_admin"
    ? organizationAdminKpis
    : defaultKpis;

  if (["support_admin", "super_admin"].includes(data.role)) {
    kpis.push({
      label: "Unassigned",
      value: data.unassigned,
      icon: Users,
      tone: "text-orange-300",
      testid: "kpi-unassigned",
      href: "/tickets?unassigned=true",
    });
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            {ROLE_LABEL[data.role]} · overview
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">
            Welcome back, <span className="gold-text">{user?.name || "User" || "there"}</span>.
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <Link key={k.label} to={k.href || "/tickets"} data-testid={k.testid}
              className="rounded-xl border border-border bg-card/70 p-4 hover:border-[#d4af37]/40 hover:bg-card transition-colors cursor-pointer group">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground group-hover:text-foreground">{k.label}</div>
                <Icon className={`h-4 w-4 ${k.tone}`} />
              </div>
              <div className={`mt-2 font-display text-3xl font-bold ${k.tone}`}>
                {k.value}
              </div>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Tickets by status" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={225}>
            <BarChart
              data={statusData}
              margin={{ top: 6, right: 8, bottom: 0, left: -8 }}
              barCategoryGap="18%"
            >
              <XAxis
                dataKey="name"
                stroke="currentColor"
                opacity={0.68}
                fontSize={10}
                interval={0}
                angle={-22}
                textAnchor="end"
                height={62}
                tickLine={false}
              />
              <YAxis
                stroke="currentColor"
                opacity={0.68}
                fontSize={10}
                allowDecimals={false}
                tickLine={false}
                width={34}
              />

              <Tooltip
                cursor={{ fill: "transparent" }}
                content={<DashboardTooltip />}
              />

              <Bar
                dataKey="value"
                radius={[5, 5, 0, 0]}
                activeBar={{
                  fill: "#111111",
                  stroke: "#000000",
                  strokeWidth: 1,
                }}
                onClick={(d) =>
                  d?.status &&
                  navigate(`/tickets?status=${d.status}`)
                }
                cursor="pointer"
              >
                {statusData.map((entry, index) => (
                  <Cell
                    key={`${entry.status}-${index}`}
                    fill={entry.color}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="By priority">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={priorityData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} stroke="#0B0C10">
                {priorityData.map((_, i) => (
                  <Cell key={i} fill={["#ef4444", "#f59e0b", "#d4af37", "#3b82f6", "#64748b"][i % 5]} />
                ))}
              </Pie>
              <Tooltip content={<DashboardTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-3 flex-wrap text-[10px] text-muted-foreground">
            {priorityData.map((p) => (
              <span key={p.name}>{PRIORITY_LABEL[p.name] || p.name}: <b className="text-foreground">{p.value}</b></span>
            ))}
          </div>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Top applications">
          <div className="space-y-2">
            {appData.length === 0 && <div className="text-xs text-muted-foreground">No data</div>}
            {appData.map((a) => (
              <div key={a.name} className="flex items-center justify-between text-xs">
                <span className="uppercase tracking-widest text-muted-foreground">{a.name}</span>
                <div className="flex-1 mx-3 h-1.5 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#d4af37] to-[#e5c158]" style={{ width: `${Math.min(100, (a.value / Math.max(...appData.map((x) => x.value))) * 100)}%` }} />
                </div>
                <span className="font-mono w-8 text-right">{a.value}</span>
              </div>
            ))}
          </div>
        </ChartCard>
        <div className="lg:col-span-2 rounded-xl border border-border bg-card/70 p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Recent tickets</div>
          <div className="divide-y divide-border">
            {data.recent.length === 0 && <div className="text-xs text-muted-foreground py-4">No tickets yet — click <b>New Ticket</b> to get started.</div>}
            {data.recent.map((t) => (
              <Link
                key={t.id}
                to={`/tickets/${t.id}`}
                data-testid={`recent-ticket-${t.ticket_number}`}
                className="flex items-center justify-between gap-3 py-3 hover:bg-white/[0.02] rounded-md px-2 -mx-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                    <span>{t.ticket_number}</span>
                    <PriorityBadge priority={t.priority} />
                  </div>
                  <div className="text-sm truncate">{t.title}</div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={t.status} />
                  {t.resolution_due && <SLATimer deadline={t.resolution_due} compact />}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DashboardTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const item = payload[0];
  const source = item?.payload || {};

  return (
    <div className="md-chart-tooltip min-w-[150px] rounded-lg border px-3 py-2 shadow-xl">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label || source.name || "Details"}
      </div>

      <div className="mt-1 flex items-center justify-between gap-5">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{
              backgroundColor:
                source.color ||
                item.color ||
                GOLD,
            }}
          />
          <span className="truncate text-sm font-medium">
            {source.name || item.name || "Tickets"}
          </span>
        </div>

        <span className="font-mono text-sm font-bold">
          {item.value ?? source.value ?? 0}
        </span>
      </div>

    </div>
  );
}


function DashboardSkeleton() {
  return (
    <div className="space-y-6" data-testid="dashboard-skeleton">
      <div className="space-y-3">
        <Skeleton className="h-3 w-36" />
        <Skeleton className="h-10 w-full max-w-xl" />
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="rounded-xl border border-border bg-card/70 p-4"
          >
            <div className="flex items-center justify-between">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-4 rounded-full" />
            </div>
            <Skeleton className="mt-4 h-9 w-16" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card/70 p-4 lg:col-span-2">
          <Skeleton className="mb-5 h-3 w-32" />
          <Skeleton className="h-[200px] w-full" />
        </div>

        <div className="rounded-xl border border-border bg-card/70 p-4">
          <Skeleton className="mb-5 h-3 w-24" />
          <Skeleton className="mx-auto h-[180px] w-[180px] rounded-full" />
        </div>
      </div>
    </div>
  );
}


function ChartCard({ title, children, className = "" }) {
  return (
    <div className={`rounded-xl border border-border bg-card/70 p-4 ${className}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">{title}</div>
      {children}
    </div>
  );
}
